// Pure helpers for the Patent Packet report's Phase-2 section generation
// (Analyze ▾ / Reports ▾ → Patent Packet). Kept separate from AppShell.jsx
// (a heavy React module) so the durable-resume decision logic and the final
// assembly shape are unit-testable without a render harness, the same
// pattern src/ai/patentStatus.js already established for the Phase-1
// prior-art search step.
//
// D7 (durability audit 2026-08-20): the five parallel askLLM section calls
// used to persist only once ALL FIVE had settled -- a browser closed at any
// point during that window lost the entire in-progress packet, even
// sections that had already finished, with zero durable trace. AppShell.jsx
// now persists each section (via persistAnalysis/updateCandidateAnalyses)
// the moment it settles, using the helpers below to decide, on a reopen,
// which of the five still need generating -- never re-spending an LLM call
// on one that already succeeded.

// Section descriptors: `fields` are the packet keys this section owns, used
// both to read a fresh LLM result into the packet and to decide, from a
// possibly-partial cached packet, whether this section still needs to run.
export const PATENT_PACKET_SECTIONS = [
  { id: 'title_abstract_field', fields: ['title', 'abstract', 'field_of_invention'] },
  { id: 'background', fields: ['background'] },
  { id: 'summary_of_invention', fields: ['summary_of_invention'] },
  { id: 'detailed_description', fields: ['detailed_description'] },
  { id: 'claims', fields: ['claims'] },
];

// Distinguishes "generated" from "still missing": a non-empty array for
// claims, non-null for every other field.
function hasSectionValue(packet, field) {
  const v = packet?.[field];
  if (Array.isArray(v)) return v.length > 0;
  return v != null;
}

/** True if every field a section owns is already present on `packet`. */
export function sectionIsComplete(packet, section) {
  return section.fields.every((f) => hasSectionValue(packet, f));
}

/**
 * Given a possibly-null cached analysisCache entry for patent_packet, decide
 * whether this is a RESUME of an interrupted generation (status
 * 'generating', written by a prior Phase-2 run that didn't finish before the
 * browser closed) and, if so, which of the five sections still need an LLM
 * call. A fresh generation (no cached entry, or one that isn't mid-Phase-2 --
 * e.g. still in the Phase-1 search/consent states, or a fully complete
 * packet) always needs all five: only an actual in-progress Phase-2 packet
 * can already have some sections filled.
 */
export function planPatentPacketGeneration(cachedPacket) {
  const resuming = cachedPacket?.status === 'generating';
  const sections = PATENT_PACKET_SECTIONS.map((section) => ({
    ...section,
    needed: !(resuming && sectionIsComplete(cachedPacket, section)),
  }));
  return { resuming, sections };
}

/**
 * Pull a section's already-cached field values into the same {ok: true,
 * ...} shape callSection() would have returned, for a section being SKIPPED
 * on resume because it's already complete.
 */
export function reuseSectionResult(cachedPacket, section) {
  const out = { ok: true };
  for (const f of section.fields) out[f] = cachedPacket?.[f] ?? (f === 'claims' ? [] : null);
  return out;
}

/**
 * Build the durable partial-packet doc persisted after each section settles
 * during Phase 2. `status: 'generating'` is what routes AppShell's
 * AnalysisPanel to PatentSearchStatusPanel (any truthy `status` means "not
 * the final packet yet") instead of PatentPacketPanel, and what
 * planPatentPacketGeneration() above checks for on a later resume.
 * `sections` carries whichever of {title, abstract, field_of_invention,
 * background, summary_of_invention, detailed_description, claims} are known
 * so far; anything absent renders as null/[] here, same as the final shape.
 */
export function buildPartialPacket({ formula, generatedAt, appendix, priorArtContext, sections }) {
  return {
    status: 'generating',
    formula,
    generated_at: generatedAt,
    appendix,
    // Not part of the final packet's shape (see assemblePatentPacket) --
    // carried only on the partial doc so a resume can rebuild the Phase-2
    // LLM context without re-running the Phase-1 search.
    prior_art_context: priorArtContext,
    ok: false,
    title: sections?.title ?? null,
    abstract: sections?.abstract ?? null,
    field_of_invention: sections?.field_of_invention ?? null,
    background: sections?.background ?? null,
    summary_of_invention: sections?.summary_of_invention ?? null,
    detailed_description: sections?.detailed_description ?? null,
    claims: sections?.claims ?? [],
    _section_errors: [],
  };
}

/**
 * Final assembly, once all five section results (r1..r5, each the {ok,
 * ...fields} shape callSection()/reuseSectionResult() produce) are in hand.
 * Identical in shape to the packet this codebase has always produced here:
 * no `status` field (see buildPartialPacket's docstring on why that field
 * matters for routing) and no `prior_art_context` leaking into the final
 * document -- "keep the final assembled packet identical in shape to
 * today" (D7's own scope boundary).
 */
export function assemblePatentPacket({ formula, generatedAt, appendix, r1, r2, r3, r4, r5 }) {
  const anyOk = r1.ok || r2.ok || r3.ok || r4.ok || r5.ok;
  return {
    formula,
    generated_at: generatedAt,
    appendix,
    ok: anyOk,
    title: r1.title || null,
    abstract: r1.abstract || null,
    field_of_invention: r1.field_of_invention || null,
    background: r2.background || null,
    summary_of_invention: r3.summary_of_invention || null,
    detailed_description: r4.detailed_description || null,
    claims: r5.claims || [],
    _section_errors: [r1, r2, r3, r4, r5].filter((r) => !r.ok).map((r) => r.error),
  };
}
